/**
 * One of TWO D1 column-name records, and the boundary is ENFORCEMENT-SCOPED rather than semantic -- the prose that
 * used to call this "the common D1 column-name SSOT" was falsified by the tree the moment the second record landed.
 *
 * `DB_COLUMN_NAMES` (`const/column-names.ts`) is the target set of the `custom/no-d1-column-name-literals` ESLint
 * rule, which derives `TARGET_LITERALS` from `Object.values(DB_COLUMN_NAMES)` and flags every raw occurrence inside a
 * SQL template literal. THIS record is deliberately outside that set. The distinction the two docstrings each stated
 * -- "identity columns here, cross-table-reuse columns there" -- does not survive contact: `MODIFIED_AT` sits in the
 * enforced record while `CREATED_AT` / `UPDATED_AT` sit here, and all three are universal audit stamps.
 *
 * DO NOT fold the two records together as a tidy-up. The fold is not neutral: it hands every member below to the
 * ESLint matcher, and `created_at` alone is authored as raw SQL text in 38 `const/` and `types/` leaves -- a fold
 * would mint that queue on day one, in SQL template contexts where the literal is the query's own text. Moving a
 * member INTO the enforced record is a deliberate, per-member act with its own adoption sweep.
 *
 * The nine property/source column members added 2026-08-24 land HERE for that reason and not as an oversight: each was
 * spelled as a bare literal across several registries that each own a DIFFERENT projection (the navigator write map,
 * the draft-update column order, the valid-column allowlist, the sanitizer allowlist, the address tuple, the source
 * bind order, the money-representation register), so the NAME is now shared while the projections stay distinct.
 * Promoting any of them into the enforced record is the separate per-member act this docstring already mandates; the
 * queue it would mint was MEASURED by replaying `QUAL-NO-D1-COLUMN-NAME-LITERALS-001` over the tree with the nine
 * members added -- 33 SQL-position rows across 23 leaves, plus whatever the AST twin's alias-prefix and list-terminal
 * probes add on top -- so the promotion owes that sweep in one change, which is why it is not this one.
 */

export const DB_COLUMNS = {
	CREATED_AT: 'created_at',
	UPDATED_AT: 'updated_at',
	USER_ID: 'user_id',
	CONVERSATION_ID: 'conversation_id',
	/** `conversations.conversation_type` -- the chats entry maps the legacy camelCase body key onto it, and the
	 *  save-message AI check reads it off the conversation row. */
	CONVERSATION_TYPE: 'conversation_type',
	PROPERTY_ID: 'property_id',
	OFFER_ID: 'offer_id',
	DURATION_MS: 'duration_ms',
	MARKET_MAKER_ID: 'market_maker_id',
	COMPLETED_AT: 'completed_at',
	STARTED_AT: 'started_at',
	AUCTION_END: 'auction_end',
	LINES_OF_CODE: 'lines_of_code',
	SECURITY_LEVEL: 'security_level',
	FILE_TYPE: 'file_type',
	REFERENCE_ID: 'reference_id',
	ACCOUNT_ID: 'account_id',
	/** `properties.district_city` -- named by the address-column tuple, the navigator write/read maps and the valid-column allowlist. */
	DISTRICT_CITY: 'district_city',
	/** `land.lot_dimensions`. */
	LOT_DIMENSIONS: 'lot_dimensions',
	/** `land.flood_zone`. DISTINCT from `properties.federal_flood_zone` -- do not collapse. */
	FLOOD_ZONE: 'flood_zone',
	/** `land.wetlands` -- named by the navigator parcel read map and the property detail reader. */
	WETLANDS: 'wetlands',
	/** `taxes.annual_tax_amount` -- also a money-representation register row. */
	ANNUAL_TAX_AMOUNT: 'annual_tax_amount',
	/** `taxes.assessment_amount` -- also a money-representation register row. */
	ASSESSMENT_AMOUNT: 'assessment_amount',
	/** `home_details.auction_end_date_time`. DISTINCT from `market_maker.federa_auction_end_date_time`. */
	AUCTION_END_DATE_TIME: 'auction_end_date_time',
	/** `home_details.property_remarks`. */
	PROPERTY_REMARKS: 'property_remarks',
	/** `source.company`. */
	COMPANY: 'company',
	/** `source.market_seller_venue`, mirrored onto `market_maker`. */
	MARKET_SELLER_VENUE: 'market_seller_venue',
} as const;
